import React, { useEffect, useRef } from 'react';

import { isEmpty } from '@utils/common';

import { MIN_GANTT_HEIGHT } from '@components/common/Gantt/constants';

import { TaskGanttContentProps } from './TaskGanttContent/TaskGanttContent';
import Calendar from '../Calendar';
import { CalendarProps } from '../Calendar/Calendar';
import Grid from '../Grid';
import { GridProps } from '../Grid/Grid';
import CurrentDateIndicator from './CurrentDateIndicator';
import TaskGanttContent from './TaskGanttContent';

import styles from './TaskGantt.module.scss';

export type TaskGanttProps = {
	gridProps: GridProps;
	calendarProps: CalendarProps;
	barProps: TaskGanttContentProps;
	ganttHeight: number;
	scrollY: number;
	scrollX: number;
};

const TaskGantt: React.FC<TaskGanttProps> = ({
	gridProps,
	calendarProps,
	barProps,
	ganttHeight,
	scrollY,
	scrollX,
}) => {
	const ganttSVGRef = useRef<SVGSVGElement>(null);
	const horizontalContainerRef = useRef<HTMLDivElement>(null);
	const verticalGanttContainerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (horizontalContainerRef.current) {
			horizontalContainerRef.current.scrollTop = scrollY;
		}
	}, [scrollY]);

	useEffect(() => {
		if (verticalGanttContainerRef.current) {
			verticalGanttContainerRef.current.scrollLeft = scrollX;
		}
	}, [scrollX]);

	const gridHeight = barProps.rowHeight * barProps.tasks.length;

	return (
		<div
			ref={verticalGanttContainerRef}
			className={styles.ganttVerticalContainer}
			dir='ltr'
		>
			<svg
				height={calendarProps.headerHeight}
				width={gridProps.svgWidth}
				xmlns='http://www.w3.org/2000/svg'
			>
				<Calendar
					columnWidth={calendarProps.columnWidth}
					dateSetup={calendarProps.dateSetup}
					headerHeight={calendarProps.headerHeight}
					locale={calendarProps.locale}
					viewMode={calendarProps.viewMode}
				/>
			</svg>
			<div
				ref={horizontalContainerRef}
				className={styles.horizontalContainer}
				style={
					ganttHeight
						? { height: ganttHeight, width: gridProps.svgWidth }
						: { width: gridProps.svgWidth }
				}
			>
				<svg
					ref={ganttSVGRef}
					height={isEmpty(gridProps.tasks) || MIN_GANTT_HEIGHT > gridHeight
						? MIN_GANTT_HEIGHT
						: gridHeight
					}
					width={gridProps.svgWidth}
					xmlns='http://www.w3.org/2000/svg'
				>
					<Grid
						columnWidth={gridProps.columnWidth}
						dates={gridProps.dates}
						rowHeight={gridProps.rowHeight}
						setTodayColumnScrollX={gridProps.setTodayColumnScrollX}
						svgWidth={gridProps.svgWidth}
						tasks={gridProps.tasks}
						todayColor={gridProps.todayColor}
					/>
					<TaskGanttContent
						arrowIndent={barProps.arrowIndent}
						ganttEvent={barProps.ganttEvent}
						onClick={barProps.onClick}
						rowHeight={barProps.rowHeight}
						setGanttEvent={barProps.setGanttEvent}
						taskHeight={barProps.taskHeight}
						tasks={barProps.tasks}
					/>
					<CurrentDateIndicator
						columnWidth={gridProps.columnWidth}
						dates={gridProps.dates}
						rowHeight={gridProps.rowHeight}
						tasks={gridProps.tasks}
					/>
				</svg>
			</div>
		</div>
	);
};

export default TaskGantt;
